import { isSameDay } from 'date-fns';
import { localStorageService } from './localStorage';

// One record per gym: the wall you follow at home and the one you visit on
// holiday have their own "since when".
const keyFor = (gymSlug: string) => `gym_visit_${gymSlug}`;

/**
 * When this browser last looked at a gym's page. Two instants, not one:
 *
 * - `lastVisitAt` is the most recent look, bumped on every open.
 * - `newSince` is the last look on an *earlier* day, and is what "new" is
 *   measured against.
 *
 * Splitting them is what makes a "New" label last the whole day. The first open
 * of a day rolls the previous day's `lastVisitAt` into `newSince`; every later
 * open that day leaves `newSince` alone — so what was new at breakfast is still
 * new at dinner, and only tomorrow does today's last look become the yardstick.
 */
interface GymVisit {
  lastVisitAt: string;
  newSince: string | null;
}

const parseInstant = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * The browser's memory of visiting a gym's page, kept so the climb list can
 * mark what went up since. Per browser, not per account: a signed-out visitor
 * gets the labels too.
 */
export const gymVisitsService = {
  /**
   * Records a look at the gym's page and answers what "new" is measured
   * against on this visit: the last look on an earlier day, or null when there
   * has not been one — the first visit ever, when nothing is "since" anything.
   * `now` is injectable for tests.
   */
  recordVisit(gymSlug: string, now: Date = new Date()): Date | null {
    const stored = localStorageService.get<GymVisit>(keyFor(gymSlug));
    const lastVisit = parseInstant(stored?.lastVisitAt);

    const newSince = !lastVisit
      ? null
      : isSameDay(lastVisit, now)
        ? parseInstant(stored?.newSince)
        : lastVisit;

    localStorageService.set<GymVisit>(keyFor(gymSlug), {
      lastVisitAt: now.toISOString(),
      newSince: newSince?.toISOString() ?? null,
    });

    return newSince;
  },
};

/**
 * Whether a climb wears the "New" label on this visit: it went up after the
 * reference visit (see `recordVisit`), or it went up today — a same-day climb
 * is news whether or not the page was opened in between, and it stays news
 * until midnight.
 */
export function isNewClimb(createdAt: string, newSince: Date | null, now: Date = new Date()): boolean {
  const created = parseInstant(createdAt);
  if (!created) return false;
  if (isSameDay(created, now)) return true;
  return newSince != null && created > newSince;
}
